import { useEffect, useState } from "react";
import { measureAccent, type AccentMeasurement, type ApplicationDraft } from "../../api/client";
import { TextField } from "../../components/TextField";
import { DocumentPreview } from "../DocumentPreview";

/**
 * Screen 06, first card · Brand.
 *
 * ADR 0007 draws the line the whole step sits on: the certification body owns its mark,
 * its legal name, its accreditation marks and one accent colour; Complao owns layout,
 * typography, table structure, section order and finding badge semantics.
 *
 * The contrast ratio is measured **by the server**, not here. The same function writes the
 * value onto the tenant at submission, so the number beside the picker is the number that
 * gets stored — a second implementation in the browser would be a second answer to the
 * question the fallback decision turns on.
 *
 * Nothing on this step can block. A failing ratio keeps the colour and shows it; only
 * rules and table headers fall back to Deep Forest.
 */
export function BrandStep({
  draft,
  set,
  markPreview,
  onMarkChosen,
}: {
  draft: ApplicationDraft;
  set: (patch: Partial<ApplicationDraft>) => void;
  /** Data URI for the chosen mark. Client-side only — see `onMarkChosen`. */
  markPreview: string | null;
  onMarkChosen: (file: File | null) => void;
}) {
  const accent = draft.brand_accent ?? "";
  const [measurement, setMeasurement] = useState<AccentMeasurement | null>(null);

  useEffect(() => {
    let current = true;
    const timer = window.setTimeout(() => {
      measureAccent(accent)
        .then((result) => {
          if (current) setMeasurement(result);
        })
        .catch(() => {
          // A measurement that cannot be taken is not a reason to stop the applicant.
          if (current) setMeasurement(null);
        });
    }, 200);
    return () => {
      current = false;
      window.clearTimeout(timer);
    };
  }, [accent]);

  return (
    <section className="card">
      <h2>Brand</h2>
      <p className="sub" style={{ marginBottom: "14px" }}>
        Applied to the audit plan and the audit report you issue. Complao owns the layout and
        typography; you control the mark, the legal name and one accent colour.
      </p>

      <div className="grid2" style={{ alignItems: "start" }}>
        <div className="field">
          <label className="f" htmlFor="brand_mark">
            Certification body mark <i>SVG or PNG, transparent or light background</i>
          </label>
          <label className="markdrop" htmlFor="brand_mark">
            {markPreview ? (
              <img src={markPreview} alt="" />
            ) : (
              <span className="sub">Click to choose a file</span>
            )}
          </label>
          <input
            id="brand_mark"
            className="visually-hidden"
            type="file"
            accept="image/svg+xml,image/png"
            onChange={(event) => onMarkChosen(event.target.files?.[0] ?? null)}
          />
          {markPreview ? (
            <div className="row" style={{ marginTop: "9px" }}>
              <button type="button" className="btn sec sm" onClick={() => onMarkChosen(null)}>
                Remove
              </button>
            </div>
          ) : null}
        </div>

        <div>
          <TextField
            id="brand_accent"
            label="Accent colour"
            hint="Six-digit hex, e.g. #1F4B7A"
            value={accent}
            onChange={(value) => set({ brand_accent: value })}
          />
          <AccentReadout measurement={measurement} accent={accent} />

          <div className="field" style={{ marginTop: "12px" }}>
            <label className="f">Accreditation marks</label>
            <label className="ack" htmlFor="accreditation_marks">
              <input
                type="checkbox"
                id="accreditation_marks"
                checked={draft.accreditation_marks ?? false}
                onChange={(event) => set({ accreditation_marks: event.target.checked })}
              />
              <span>
                Show my accreditation body mark on issued documents
                <small>Suppressed on every unsigned draft, alongside your logo.</small>
              </span>
            </label>
          </div>
        </div>
      </div>

      <hr className="hr" />

      <div className="eyebrow">Preview</div>
      <DocumentPreview
        legalName={draft.legal_name ?? ""}
        markPreview={markPreview}
        ruleColour={measurement?.rule_colour ?? null}
      />
    </section>
  );
}

/**
 * What the accent measured, and what it costs.
 *
 * A failing ratio is a warning, never an error — the wording says the mark and the colour
 * still appear, because the applicant's next question is always whether their brand
 * survived.
 */
function AccentReadout({
  measurement,
  accent,
}: {
  measurement: AccentMeasurement | null;
  accent: string;
}) {
  if (!accent.trim()) {
    return (
      <p className="sub">
        Not set. Documents will carry your legal name in Complao&rsquo;s own palette.
      </p>
    );
  }
  if (!measurement || measurement.contrast === null || measurement.contrast === undefined) {
    return <p className="sub">Not a six-digit hex colour yet.</p>;
  }

  const ratio = measurement.contrast.toFixed(2);
  if (!measurement.document_fallback) {
    return (
      <div className="okbar">
        Contrast {ratio}:1 on the document surface. Accepted — rules and table headers will
        use your colour.
      </div>
    );
  }

  return (
    <div className="warnbar">
      <b>Contrast {ratio}:1 — below the 4.5:1 minimum.</b> Your mark and your colour still
      appear, but rules and table headers fall back to Deep Forest so the document stays
      readable when printed in greyscale or viewed by a low-vision assessor. This does not
      hold up your application.
    </div>
  );
}
